/**
 * <HelpBubble> — le bouton d'aide d'un header (molecule).
 *
 * L'équivalent maison du `HelpFAB` legacy : le bouton qui ouvre la messagerie du support. Son
 * glyphe est le point d'interrogation cerclé, pas une bulle : la bulle est déjà « Écrire à un
 * tuteur » sur l'accueil, et deux bulles pour deux destinataires se confondent. Il n'ouvre pas
 * d'onglet, parce qu'Intercom s'ouvre par-dessus l'app et prend tout l'écran — un onglet
 * promettrait une page qui n'existe pas.
 *
 * Une pastille pleine de 44 px, sans ombre, comme la pastille de profil qu'elle côtoie
 * (`surface-raised`, le glyphe à la couleur de l'univers). Elle compose `<IconButton>` et, quand
 * il y a des réponses non lues, `<Badge>`. Elle ne se place jamais elle-même.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { cappedCount } from "../../utils/cappedCount"

export type HelpBubbleProps = {
    onPress?: () => void
    /** Réponses du support non lues — au-delà de 9, la pastille dit « 9+ ». */
    unreadCount?: number
    /** Libellé lu par les lecteurs d'écran — défaut « Contacter le support ». */
    accessibilityLabel?: string
    testID?: string
}

/** La cible tactile du livre, à la hauteur de la pastille de profil. */
const SIZE = 44

/** Le glyphe : la taille intermédiaire du livre (16 · 20 · 24). */
const ICON_SIZE = 20

export function HelpBubble(props: HelpBubbleProps) {
    const { onPress, unreadCount = 0, accessibilityLabel = "Contacter le support", testID } = props

    return (
        <View testID={testID} style={styles.bubble}>
            <IconButton
                iconName="question-circle"
                color="surface-raised"
                iconColor="role"
                size={SIZE}
                iconSize={ICON_SIZE}
                onPress={onPress}
                accessibilityLabel={accessibilityLabel}
            />
            {unreadCount > 0 && (
                <View style={styles.badge} pointerEvents="none">
                    <Badge text={cappedCount(unreadCount)} variant="danger" />
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    bubble: {
        width: SIZE,
        height: SIZE,
        borderRadius: SIZE / 2,
    },
    badge: {
        position: "absolute",
        top: -theme.spacing["1"],
        right: -theme.spacing["1"],
    },
}))
